import type { IButtonProps } from '@lego/klik-ui';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Icon,
  Input,
  Popover,
  Portal,
  Text,
  Tooltip,
} from '@lego/klik-ui';
import { MinusBold } from '@lego/klik-ui/icons';
import { type MouseEvent, useMemo, useState } from 'react';
import type { EnrichedDeviceNetworkConfig } from '../common/table/column_definitions/DeviceNetworkConfig';

export type UnassignResult = {
  success: boolean;
  failures?: { networkConfigId: number; error: string }[];
};

type UnassignNetworkConfigPopoverProps = IButtonProps & {
  assignedConfigs: EnrichedDeviceNetworkConfig[];
  onUnassign: (networkConfigIds: number[]) => Promise<UnassignResult>;
};

export const UnassignNetworkConfigPopover = ({
  assignedConfigs,
  onUnassign,
  ...buttonProps
}: UnassignNetworkConfigPopoverProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [configErrors, setConfigErrors] = useState<Map<number, string>>(new Map());

  const filteredConfigs = useMemo(() => {
    if (!searchQuery.trim()) return assignedConfigs;
    const query = searchQuery.toLowerCase();
    return assignedConfigs.filter(
      (c) =>
        (c.label?.toLowerCase().includes(query) ?? false) ||
        (c.fileName?.toLowerCase().includes(query) ?? false) ||
        String(c.networkConfigId).includes(query)
    );
  }, [assignedConfigs, searchQuery]);

  const handleToggle = (configId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(configId)) {
        next.delete(configId);
      } else {
        next.add(configId);
      }
      return next;
    });
    setConfigErrors((prev) => {
      if (!prev.has(configId)) return prev;
      const next = new Map(prev);
      next.delete(configId);
      return next;
    });
  };

  const handleUnassign = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedIds.size === 0) return;

    setIsUnassigning(true);
    setConfigErrors(new Map());
    const result = await onUnassign([...selectedIds]);
    setIsUnassigning(false);

    if (result.success) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setConfigErrors(new Map());
      setIsOpen(false);
    } else if (result.failures) {
      const errors = new Map<number, string>();
      for (const failure of result.failures) {
        errors.set(failure.networkConfigId, failure.error);
      }
      setConfigErrors(errors);
      const failedIds = new Set(result.failures.map((f) => f.networkConfigId));
      setSelectedIds((prev) => new Set([...prev].filter((id) => failedIds.has(id))));
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchQuery('');
    setConfigErrors(new Map());
    setIsOpen(false);
  };

  return (
    <Popover isLazy isOpen={isOpen} onClose={handleClose} placement="bottom-end">
      <Popover.Trigger>
        <Box onClick={(e: MouseEvent) => e.preventDefault()}>
          <Tooltip label="Unassign network configs from this device" placement="left">
            <Button
              {...buttonProps}
              leftIcon={<Icon as={MinusBold} />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
              size="sm"
              variant="outline"
            >
              Unassign
            </Button>
          </Tooltip>
        </Box>
      </Popover.Trigger>
      <Portal>
        <Popover.Content onClick={(e: MouseEvent) => e.stopPropagation()} p={4} width="360px">
          <Popover.Arrow />
          <Popover.Body p={0}>
            {assignedConfigs.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                No network configs assigned.
              </Text>
            ) : (
              <>
                <Input
                  autoFocus
                  mb={2}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search assigned configs..."
                  size="sm"
                  value={searchQuery}
                />
                <Box maxH="250px" overflowY="auto">
                  {filteredConfigs.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      No configs match your search.
                    </Text>
                  ) : (
                    <CheckboxGroup>
                      {filteredConfigs.map((config) => {
                        const errorMsg = configErrors.get(config.networkConfigId);
                        return (
                          <Box key={config.networkConfigId} p={1}>
                            <Flex>
                              <Checkbox
                                isChecked={selectedIds.has(config.networkConfigId)}
                                onChange={() => handleToggle(config.networkConfigId)}
                              >
                                <Text fontSize="sm">
                                  {config.label ?? `Config #${config.networkConfigId}`}
                                  {config.fileName ? ` (${config.fileName})` : ''}
                                </Text>
                              </Checkbox>
                            </Flex>
                            {errorMsg && (
                              <Text color="red.500" fontSize="xs" ml={6}>
                                {errorMsg}
                              </Text>
                            )}
                          </Box>
                        );
                      })}
                    </CheckboxGroup>
                  )}
                </Box>
                <Button
                  colorScheme="error"
                  isDisabled={selectedIds.size === 0}
                  isLoading={isUnassigning}
                  mt={3}
                  onClick={handleUnassign}
                  size="sm"
                  variant="primary"
                  width="100%"
                >
                  Unassign {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                </Button>
              </>
            )}
          </Popover.Body>
        </Popover.Content>
      </Portal>
    </Popover>
  );
};
